import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, ChevronDown, ChevronRight,
  Zap, ArrowLeft, Copy, Download
} from 'lucide-react';
import { useAppStore } from '../store';
import {
  generateSequence, matchMachines, selectTemplate,
  MACHINES, TEMPLATES, MATERIALS, MachineId
} from '../engineeringRules';
import SequenceVisualiser from '../components/SequenceVisualiser';

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const job = useAppStore(s => s.getJob(id!));
  const updateJob = useAppStore(s => s.updateJob);

  const [activeTab, setActiveTab] = useState<'overview' | 'sequence' | 'machines' | 'process' | 'toolcodes'>('overview');
  const [expandedStn, setExpandedStn] = useState<number | null>(1);
  const [generating, setGenerating] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<MachineId | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (job?.selected_machine) setSelectedMachine(job.selected_machine as MachineId);
  }, [job?.selected_machine]);

  if (!job) return <div style={{ padding: 40, color: 'var(--forge-text3)' }}>Job not found.</div>;
  const part = job.part;
  if (!part) return <div style={{ padding: 40, color: 'var(--forge-text3)' }}>No part data.</div>;

  const { template, confidence, reasoning } = selectTemplate(part);
  const machineMatches = matchMachines(part, template);
  const eligibleMachines = machineMatches.filter(m => m.eligible);
  const seq = job.sequence;
  const tpl = TEMPLATES[template];
  const mat = MATERIALS[part.material_id ?? 'SAE1010'];

  const handleGenerate = () => {
    const machine = selectedMachine ?? eligibleMachines[0]?.machine_id;
    if (!machine) return;
    setGenerating(true);
    setTimeout(() => {
      const newSeq = generateSequence(part, machine);
      updateJob(job.id, { sequence: newSeq, selected_machine: machine, status: 'PLANNING' });
      setGenerating(false);
      setActiveTab('sequence');
    }, 700);
  };

  const handleApprove = () => updateJob(job.id, {
    status: 'APPROVED', approved_at: new Date().toISOString()
  });

  const copyToolCodes = () => {
    if (!seq) return;
    const lines = seq.stations.map((s, i) =>
      `${seq.tool_code_prefix}${String(s.station).padStart(3, '0')}${i + 1} — ${s.label}`
    ).join('\n');
    navigator.clipboard.writeText(lines);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/jobs')} style={{ marginBottom: 10 }}>
          <ArrowLeft size={12} /> Jobs
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-mono)', fontSize: 20 }}>{job.id}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {job.part_no && <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--forge-accent)' }}>{job.part_no}</span>}
              {job.customer && <span style={{ fontSize: 13, color: 'var(--forge-text2)' }}>· {job.customer}</span>}
              {job.description && <span style={{ fontSize: 12, color: 'var(--forge-text3)' }}>· {job.description}</span>}
              <StatusBadge status={job.status} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(job.status === 'REVIEW' || job.status === 'DRAFT') && (
              <button className="btn btn-primary" onClick={handleGenerate}
                disabled={generating || eligibleMachines.length === 0}>
                {generating
                  ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Generating…</>
                  : <><Zap size={13} /> Generate Sequence</>}
              </button>
            )}
            {job.status === 'PLANNING' && (
              <button className="btn btn-primary" onClick={handleApprove}>
                <CheckCircle size={13} /> Approve Plan
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'sequence', label: 'Forging Sequence' },
          { key: 'machines', label: 'Machine Analysis' },
          { key: 'process', label: 'Process Flow' },
          { key: 'toolcodes', label: 'Tool Codes' },
        ].map(({ key, label }) => (
          <button key={key} className={`tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key as any)}>
            {label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-header"><h3>Part Details</h3></div>
            <DetailRow label="Part type" value={part.part_type} />
            <DetailRow label="Nominal Ø" value={`Ø${part.nominal_diameter_mm}mm`} />
            <DetailRow label="Thread" value={`M${part.nominal_diameter_mm}×${part.thread_pitch_mm}P`} />
            <DetailRow label="Total length" value={`${part.total_length_mm}mm`} />
            {part.shank_length_mm && <DetailRow label="Shank length" value={`${part.shank_length_mm}mm`} />}
            {part.nut_af_mm && <DetailRow label="A/F (across flats)" value={`${part.nut_af_mm}mm`} />}
            {part.nut_height_mm && <DetailRow label="Nut height" value={`${part.nut_height_mm}mm`} />}
            {part.wire_diameter_mm && <DetailRow label="Wire Ø" value={`Ø${part.wire_diameter_mm}mm`} />}
            {part.blank_length_mm && <DetailRow label="Blank length" value={`${part.blank_length_mm}mm`} />}
            {part.weight_grams && <DetailRow label="Part weight" value={`${part.weight_grams}g`} />}
            <DetailRow label="Material" value={`${part.material_id} — Grade ${mat.grade}`} />
            <DetailRow label="Hollomon K" value={`${mat.k_mpa} MPa`} />
            <DetailRow label="Hollomon n" value={`${mat.n}`} />
            {part.head_type && <DetailRow label="Head type" value={part.head_type.replace(/_/g, ' ')} />}
            {part.has_flange && <DetailRow label="Flange" value="Yes" />}
            {part.has_serration && <DetailRow label="Serration" value="Yes" />}
            {part.has_nylon_insert && <DetailRow label="Nylon insert" value="Yes" />}
            {mat.ht_quench_c && <DetailRow label="HT quench" value={`${mat.ht_quench_c}°C`} />}
            {mat.ht_temper_c && <DetailRow label="HT temper" value={`${mat.ht_temper_c}°C`} />}
            {mat.target_hrc && <DetailRow label="Target HRC" value={mat.target_hrc} />}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Template card */}
            <div className="card">
              <div className="card-header"><h3>Template Selection</h3></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <div style={{
                  fontSize: 36, fontWeight: 800, fontFamily: 'var(--font-display)',
                  color: 'var(--forge-accent)', letterSpacing: '-0.04em', lineHeight: 1
                }}>{template}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{tpl.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--forge-text2)', marginTop: 2 }}>{tpl.description}</div>
                  <div style={{ marginTop: 4 }}>
                    <span className={`badge ${tpl.family === 'BOLT' ? 'badge-orange' : 'badge-blue'}`}>{tpl.family}</span>
                    <span className="badge badge-gray" style={{ marginLeft: 4 }}>{tpl.stations} stations</span>
                  </div>
                </div>
                <span className={`badge ${confidence > 0.85 ? 'badge-green' : 'badge-yellow'}`}>
                  {(confidence * 100).toFixed(0)}% conf.
                </span>
              </div>
              {reasoning.map((r, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--forge-text3)', padding: '3px 0', borderBottom: i < reasoning.length - 1 ? '1px solid var(--forge-border)' : 'none' }}>
                  <span style={{ color: 'var(--forge-accent)', marginRight: 5 }}>›</span>{r}
                </div>
              ))}
            </div>

            {/* Machine picker */}
            <div className="card">
              <div className="card-header"><h3>Machine</h3></div>
              {eligibleMachines.length === 0 ? (
                <div className="alert alert-error">
                  <AlertTriangle size={14} /> No eligible machines for this part spec.
                </div>
              ) : (
                eligibleMachines.slice(0, 4).map(m => (
                  <div key={m.machine_id}
                    onClick={() => setSelectedMachine(m.machine_id as MachineId)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '9px 10px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      background: selectedMachine === m.machine_id ? 'var(--forge-accent-dim)' : 'var(--forge-surface2)',
                      border: `1px solid ${selectedMachine === m.machine_id ? 'rgba(232,124,46,0.3)' : 'var(--forge-border)'}`,
                    }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{MACHINES[m.machine_id as MachineId].name}</div>
                      <div style={{ fontSize: 10, color: 'var(--forge-text3)', marginTop: 1 }}>
                        Force util. {m.force_utilisation_pct?.toFixed(0)}%
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress-bar" style={{ width: 60 }}>
                        <div className="progress-fill" style={{
                          width: `${Math.min(m.force_utilisation_pct ?? 0, 100)}%`,
                          background: (m.force_utilisation_pct ?? 0) > 85 ? 'var(--forge-red)' : 'var(--forge-accent)'
                        }} />
                      </div>
                      {selectedMachine === m.machine_id && <CheckCircle size={13} color="var(--forge-accent)" />}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Engineer notes */}
            {job.engineer_notes && (
              <div className="card">
                <div className="card-header"><h3>Engineer Notes</h3></div>
                <p style={{ fontSize: 12, color: 'var(--forge-text2)', lineHeight: 1.7 }}>{job.engineer_notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SEQUENCE ── */}
      {activeTab === 'sequence' && (
        <div>
          {!seq ? (
            <div className="empty-state">
              <p style={{ marginBottom: 12 }}>No sequence generated yet.</p>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || eligibleMachines.length === 0}>
                <Zap size={13} /> Generate Sequence
              </button>
            </div>
          ) : (
            <>
              {seq.warnings.length > 0 && (
                <div className="alert alert-warning" style={{ marginBottom: 16 }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                  <div>{seq.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>
                </div>
              )}

              {/* Summary strip */}
              <div style={{
                display: 'flex', gap: 20, flexWrap: 'wrap',
                padding: '14px 18px', marginBottom: 20,
                background: 'var(--forge-surface2)', borderRadius: 8,
                border: '1px solid var(--forge-border)'
              }}>
                <Pill label="Machine" value={MACHINES[seq.machine_id as MachineId]?.name ?? seq.machine_id} />
                <Pill label="Wire Ø" value={`Ø${seq.wire_diameter_mm.toFixed(2)}mm`} />
                <Pill label="Blank" value={`${seq.blank_length_mm.toFixed(1)}mm`} />
                <Pill label="L/D" value={seq.ld_ratio.toFixed(2)} />
                <Pill label="Template" value={seq.template} accent />
                <Pill label="Stations" value={`${seq.stations.length}`} />
                <Pill label="Tool prefix" value={seq.tool_code_prefix} mono />
              </div>

              {/* Visual */}
              <div className="card" style={{ marginBottom: 20, padding: '16px 16px 20px' }}>
                <div style={{ fontSize: 11, color: 'var(--forge-text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Forging Sequence Visualisation
                </div>
                <SequenceVisualiser seq={seq} />
              </div>

              {/* Station detail cards */}
              {seq.stations.map(stn => (
                <div key={stn.station} style={{ marginBottom: 8 }}>
                  <div
                    onClick={() => setExpandedStn(expandedStn === stn.station ? null : stn.station)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px',
                      borderRadius: expandedStn === stn.station ? '8px 8px 0 0' : 8,
                      background: 'var(--forge-surface)',
                      border: '1px solid var(--forge-border)',
                      cursor: 'pointer',
                    }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: stn.ko_type === 'ACTIVE' ? 'var(--forge-green)' : 'var(--forge-accent)',
                      color: '#0a0b0d',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>{stn.station}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{stn.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--forge-text3)', marginTop: 1 }}>
                        {stn.operation.slice(0, 90)}{stn.operation.length > 90 ? '…' : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <KoBadge type={stn.ko_type} />
                      <MatBadge mat={stn.punch_insert_material} />
                      {stn.pallet_material !== 'NONE' && (
                        <span className="badge badge-gray" style={{ fontSize: 9 }}>{stn.pallet_material} pallet</span>
                      )}
                      {expandedStn === stn.station
                        ? <ChevronDown size={13} color="var(--forge-text3)" />
                        : <ChevronRight size={13} color="var(--forge-text3)" />}
                    </div>
                  </div>

                  {expandedStn === stn.station && (
                    <div style={{
                      background: 'var(--forge-surface2)',
                      border: '1px solid var(--forge-border)', borderTop: 'none',
                      borderRadius: '0 0 8px 8px', padding: 16
                    }}>
                      <p style={{ fontSize: 12, color: 'var(--forge-text2)', marginBottom: 14, lineHeight: 1.7 }}>
                        {stn.operation}
                      </p>
                      <div className="grid-2" style={{ gap: 8, marginBottom: 12 }}>
                        {stn.die_bore_mm && <MiniRow label="Die bore" value={`Ø${stn.die_bore_mm.toFixed(2)}mm`} />}
                        {stn.cone_angle_deg && <MiniRow label="Cone angle" value={`${stn.cone_angle_deg}°`} />}
                        {stn.entry_angle_deg && <MiniRow label="Entry angle" value={`${stn.entry_angle_deg}°`} />}
                        {stn.tc_pallet && <MiniRow label="TC pallet" value={stn.tc_pallet} />}
                        <MiniRow label="KO type" value={stn.ko_type} />
                        <MiniRow label="Punch insert" value={stn.punch_insert_material} />
                        <MiniRow label="Pallet" value={stn.pallet_material} />
                      </div>
                      {stn.notes.filter(Boolean).length > 0 && (
                        <div style={{ borderTop: '1px solid var(--forge-border)', paddingTop: 10 }}>
                          {stn.notes.filter(Boolean).map((n, i) => (
                            <div key={i} style={{ fontSize: 11, color: 'var(--forge-text3)', padding: '2px 0', display: 'flex', gap: 6 }}>
                              <span style={{ color: 'var(--forge-accent)', flexShrink: 0 }}>›</span>{n}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Tool code */}
                      <div style={{ marginTop: 10, padding: '6px 10px', background: 'var(--forge-surface3)', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: 'var(--forge-text3)' }}>Tool code prefix</span>
                        <span className="mono-val">{seq.tool_code_prefix}{String(stn.station).padStart(3, '0')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ── MACHINES ── */}
      {activeTab === 'machines' && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid var(--forge-border)' }}>
            <h3>Machine Compatibility — Template {template}</h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Type</th>
                  <th>Wire range</th>
                  <th>Force</th>
                  <th>Utilisation</th>
                  <th>Result</th>
                  <th>Blockers</th>
                </tr>
              </thead>
              <tbody>
                {machineMatches.map(m => {
                  const mach = MACHINES[m.machine_id as MachineId];
                  return (
                    <tr key={m.machine_id} style={{ opacity: m.eligible ? 1 : 0.55 }}>
                      <td style={{ fontWeight: m.eligible ? 600 : 400 }}>{m.machine_name}</td>
                      <td><span className="tag">{mach.type}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>Ø{mach.wire_min}–{mach.wire_max}mm</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{(mach.force_kg / 1000).toFixed(0)}t</td>
                      <td>
                        {m.force_utilisation_pct !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div className="progress-bar" style={{ width: 52 }}>
                              <div className="progress-fill" style={{
                                width: `${Math.min(m.force_utilisation_pct, 100)}%`,
                                background: m.force_utilisation_pct > 85 ? 'var(--forge-red)' : 'var(--forge-accent)'
                              }} />
                            </div>
                            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{m.force_utilisation_pct.toFixed(0)}%</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {m.eligible
                          ? <span className="badge badge-green"><CheckCircle size={8} /> Eligible</span>
                          : <span className="badge badge-red">Blocked</span>}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--forge-text3)', maxWidth: 240 }}>
                        {m.blockers.length > 0 ? m.blockers.join(' · ') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PROCESS FLOW ── */}
      {activeTab === 'process' && (
        <div style={{ maxWidth: 520 }}>
          {!seq ? (
            <div className="empty-state"><p>Generate sequence first.</p></div>
          ) : (
            <div className="card">
              <div className="card-header">
                <h3>Process Flow Chart</h3>
                <span style={{ fontSize: 10, color: 'var(--forge-text3)', fontFamily: 'var(--font-mono)' }}>
                  SFX-F · {new Date().toLocaleDateString('en-IN')}
                </span>
              </div>
              {/* Part header */}
              <div style={{ background: 'var(--forge-surface2)', borderRadius: 6, padding: '10px 12px', marginBottom: 20, fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: 'var(--forge-text3)' }}>Part</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--forge-text)' }}>{job.part_no || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: 'var(--forge-text3)' }}>Material</span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{part.material_id} · Grade {mat.grade}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--forge-text3)' }}>Machine</span>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{MACHINES[seq.machine_id as MachineId]?.name}</span>
                </div>
              </div>

              {seq.process_flow.map((step, i) => {
                const isLast = i === seq.process_flow.length - 1;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        background: i === 0 ? 'var(--forge-blue-dim)' : i === 1 ? 'var(--forge-accent-dim)' : 'var(--forge-surface3)',
                        border: `1px solid ${i === 0 ? 'rgba(74,158,255,0.3)' : i === 1 ? 'rgba(232,124,46,0.3)' : 'var(--forge-border)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 600,
                        color: i === 0 ? 'var(--forge-blue)' : i === 1 ? 'var(--forge-accent)' : 'var(--forge-text3)',
                      }}>{i + 1}</div>
                      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 16 }}>
                        <div style={{ fontSize: 12, fontWeight: i < 2 ? 600 : 400, color: i < 2 ? 'var(--forge-text)' : 'var(--forge-text2)' }}>
                          {step.split(' — ')[0]}
                        </div>
                        {step.includes(' — ') && (
                          <div style={{ fontSize: 11, color: 'var(--forge-text3)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                            {step.split(' — ')[1]}
                          </div>
                        )}
                        {/* Connector line */}
                        {!isLast && (
                          <div style={{ width: 1, height: 8, background: 'var(--forge-border)', marginLeft: -22, marginTop: 4 }} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TOOL CODES ── */}
      {activeTab === 'toolcodes' && (
        <div>
          {!seq ? (
            <div className="empty-state"><p>Generate sequence first.</p></div>
          ) : (
            <div className="card" style={{ maxWidth: 640 }}>
              <div className="card-header">
                <h3>Tool Code Register</h3>
                <button className="btn btn-secondary btn-sm" onClick={copyToolCodes}>
                  <Copy size={11} /> {copied ? 'Copied!' : 'Copy all'}
                </button>
              </div>

              <div style={{ fontSize: 11, color: 'var(--forge-text3)', marginBottom: 16, fontFamily: 'var(--font-mono)' }}>
                Prefix: <span className="mono-val">{seq.tool_code_prefix}</span>
              </div>

              {seq.stations.map((stn, i) => {
                const toolCode = `${seq.tool_code_prefix}${String(stn.station).padStart(3, '0')}${String(i + 1).padStart(2, '0')}`;
                return (
                  <div key={stn.station} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '10px 0', borderBottom: '1px solid var(--forge-border)'
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'var(--forge-accent-dim)', border: '1px solid rgba(232,124,46,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, color: 'var(--forge-accent)', flexShrink: 0,
                    }}>{stn.station}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--forge-accent)', marginBottom: 3 }}>
                        {toolCode}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--forge-text2)' }}>{stn.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--forge-text3)', marginTop: 2 }}>
                        {[
                          stn.punch_insert_material !== 'NONE' ? `Insert: ${stn.punch_insert_material}` : null,
                          stn.pallet_material !== 'NONE' ? `Pallet: ${stn.pallet_material}` : null,
                          stn.tc_pallet ? `TC: ${stn.tc_pallet}` : null,
                          `KO: ${stn.ko_type}`,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Hole punch universal */}
              {part.part_type === 'NUT' && (
                <div style={{ padding: '10px 0', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--forge-blue-dim)', border: '1px solid rgba(74,158,255,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700, color: 'var(--forge-blue)', flexShrink: 0,
                  }}>6</div>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--forge-blue)', marginBottom: 3 }}>
                      {seq.tool_code_prefix}9999 — HOLE PUNCH (Universal)
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--forge-text2)' }}>Stn 6 — Pierce through-bore</div>
                    <div style={{ fontSize: 10, color: 'var(--forge-text3)', marginTop: 2 }}>
                      M2 · Bore Ø{((part.nominal_diameter_mm * 0.84) - 0.15).toFixed(2)}mm · No KO (slug free-fall) · Shared/universal component
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Small helpers ──
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    DRAFT: { cls: 'badge-gray', label: 'Draft' },
    PARSING: { cls: 'badge-yellow', label: 'Parsing' },
    REVIEW: { cls: 'badge-blue', label: 'Review' },
    PLANNING: { cls: 'badge-orange', label: 'Planning' },
    APPROVED: { cls: 'badge-green', label: 'Approved' },
    COMPLETE: { cls: 'badge-green', label: 'Complete' },
  };
  const { cls, label } = map[status] ?? { cls: 'badge-gray', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--forge-border)', fontSize: 12 }}>
      <span style={{ color: 'var(--forge-text2)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--forge-text)', textAlign: 'right', maxWidth: 220 }}>{value}</span>
    </div>
  );
}

function MiniRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 8px', background: 'var(--forge-surface3)', borderRadius: 4, fontSize: 11 }}>
      <span style={{ color: 'var(--forge-text3)' }}>{label}</span>
      <span className="mono-val">{value}</span>
    </div>
  );
}

function Pill({ label, value, accent, mono }: { label: string; value: string; accent?: boolean; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: 'var(--forge-text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 13, fontWeight: 600,
        color: accent ? 'var(--forge-accent)' : 'var(--forge-text)',
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-display)',
        letterSpacing: accent ? '-0.02em' : 'normal',
      }}>{value}</div>
    </div>
  );
}

function KoBadge({ type }: { type: string }) {
  if (type === 'ACTIVE') return <span className="badge badge-green" style={{ fontSize: 9 }}>Active KO</span>;
  if (type === 'PLAIN') return <span className="badge badge-gray" style={{ fontSize: 9 }}>Plain KO</span>;
  return <span className="badge badge-gray" style={{ fontSize: 9 }}>No KO</span>;
}

function MatBadge({ mat }: { mat: string }) {
  const c: Record<string, string> = { D2: 'badge-blue', M2: 'badge-gray', M42: 'badge-yellow', TC: 'badge-orange', H13: 'badge-gray', NONE: 'badge-gray' };
  return <span className={`badge ${c[mat] ?? 'badge-gray'}`} style={{ fontSize: 9 }}>{mat}</span>;
}
